import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { toast } from "sonner";
import { WhatsappLogo, PaperPlaneTilt, CheckCircle, Warning, ArrowsClockwise, Plus, Trash, Broom } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const StatusBadge = ({ status }) => {
  const s = String(status || "pending").toLowerCase();
  const cls = s === "approved" || s === "active"
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
    : s === "rejected"
    ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
    : "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${cls}`}>{status || "pending"}</span>;
};

const toE164 = (s) => {
  const d = (s || "").replace(/[^\d+]/g, "");
  return d.startsWith("+") ? d : "+" + d.replace(/\D/g, "");
};

const bodyOf = (t) => (t?.components || []).find((c) => String(c?.type).toUpperCase() === "BODY");
const paramCount = (t) => ((bodyOf(t)?.text || "").match(/\{\{\s*\d+\s*\}\}/g) || []).length;

export default function WhatsApp() {
  const qc = useQueryClient();
  const [to, setTo] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: status } = useQuery({ queryKey: ["wa-status"], queryFn: async () => (await api.get("/whatsapp/status")).data });
  const { data: inbox } = useQuery({ queryKey: ["wa-inbox"], queryFn: async () => (await api.get("/whatsapp/inbox")).data });
  const configured = status?.configured;
  const messages = inbox?.messages || [];
  const { data: tpls } = useQuery({ queryKey: ["wa-templates"], queryFn: async () => (await api.get("/whatsapp/templates")).data, enabled: !!configured });
  const approved = tpls?.approved || [];
  const HELLO = { id: "hello_world", name: "hello_world", language: "en_US", status: "approved", components: [{ type: "BODY", text: "Hello World" }] };
  const options = approved.some((t) => t.name === "hello_world") ? approved : [HELLO, ...approved];
  const [tplTo, setTplTo] = useState("");
  const [tplName, setTplName] = useState("");
  const [tplParams, setTplParams] = useState([]);
  const [tplBusy, setTplBusy] = useState(false);
  const selectedTpl = options.find((t) => t.name === tplName);
  const { user } = useAuth();
  const isStaff = ["platform_owner", "admin"].includes(user?.role);
  const allTemplates = tpls?.templates || [];
  const [tpl, setTpl] = useState({ name: "", category: "UTILITY", language: "en_US", body_text: "", example: "", header_text: "", footer_text: "" });
  const [creating, setCreating] = useState(false);
  const createTemplate = async () => {
    if (!tpl.name.trim() || !tpl.body_text.trim()) { toast.error("Template name and message are required"); return; }
    setCreating(true);
    try {
      const example = tpl.example.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/admin/whatsapp/templates", {
        name: tpl.name.trim().toLowerCase().replace(/\s+/g, "_"),
        category: tpl.category, language: tpl.language, body_text: tpl.body_text.trim(),
        example: example.length ? example : undefined,
        header_text: tpl.header_text.trim() || undefined, footer_text: tpl.footer_text.trim() || undefined,
      });
      toast.success("Template submitted to Meta for approval");
      setTpl({ name: "", category: "UTILITY", language: "en_US", body_text: "", example: "", header_text: "", footer_text: "" });
      qc.invalidateQueries({ queryKey: ["wa-templates"] });
    } catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["wa-status"] });
    qc.invalidateQueries({ queryKey: ["wa-inbox"] });
    toast.info("Re-checking WhatsApp connection…");
  };

  const deleteMsg = async (id) => {
    try { await api.delete(`/whatsapp/messages/${id}`); qc.invalidateQueries({ queryKey: ["wa-inbox"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const clearHistory = async () => {
    if (!window.confirm("Clear all WhatsApp history? This can't be undone.")) return;
    try {
      const { data } = await api.post("/whatsapp/messages/clear", {});
      toast.success(`Cleared ${data.deleted} message(s)`);
      qc.invalidateQueries({ queryKey: ["wa-inbox"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const send = async () => {    const num = toE164(to);
    if (num.length < 8) { toast.error("Enter a valid number in international format"); return; }
    if (!text.trim()) { toast.error("Enter a message"); return; }
    setBusy(true);
    try {
      await api.post("/whatsapp/send", { to: num, text });
      toast.success("WhatsApp message sent");
      setText("");
      qc.invalidateQueries({ queryKey: ["wa-inbox"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const sendTemplate = async () => {
    const num = toE164(tplTo);
    if (num.length < 8) { toast.error("Enter a valid number in international format"); return; }
    if (!tplName) { toast.error("Choose a template"); return; }
    setTplBusy(true);
    try {
      await api.post("/whatsapp/send-template", { to: num, template: tplName, language: selectedTpl?.language || "en_US", body_params: tplParams });
      toast.success("Template message sent");
      setTplTo("");
      qc.invalidateQueries({ queryKey: ["wa-inbox"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setTplBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#25D366]/15 text-[#25D366]"><WhatsappLogo size={28} weight="fill" /></div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">WhatsApp Business</h1>
          <p className="mt-1 text-muted-foreground">Reach customers on the world's most-used chat app.</p>
        </div>
      </div>

      {status && (
        <div data-testid="wa-status" className={`flex items-start gap-3 rounded-2xl border p-4 ${configured ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/30" : "border-amber-300 bg-amber-50/60 dark:bg-amber-950/30"}`}>
          {configured ? <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" /> : <Warning size={20} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />}
          <div className="text-sm">
            {configured
              ? <p><span className="font-semibold">Connected.</span> Sending from {status.senders?.join(", ")}.</p>
              : (
                <>
                  <p className="font-semibold">WhatsApp Business isn't connected yet.</p>
                  <p className="mt-1 text-muted-foreground">Connect a verified WhatsApp sender on your iYeneh account — it activates here automatically, no extra keys needed.</p>
                  <ol className="mt-2 list-decimal space-y-1 pl-4 text-muted-foreground">
                    <li>Verify your business on <a className="font-medium text-primary underline" href="https://business.facebook.com/" target="_blank" rel="noreferrer">Meta Business Manager</a>.</li>
                    <li>Ask iYeneh support to link your WhatsApp Business Account to your iYeneh messaging line.</li>
                    <li>Register one of your iYeneh numbers to your WhatsApp Business Account and verify it (SMS/voice code).</li>
                    <li>When the number shows as active, tap <span className="font-medium">Refresh</span> below — you're connected.</li>
                  </ol>
                  <button data-testid="wa-refresh" onClick={refresh}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-400 px-4 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40">
                    <ArrowsClockwise size={14} weight="bold" /> Refresh status
                  </button>
                </>
              )}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">New message</h2>
          <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recipient</label>
          <input data-testid="wa-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+1 202 555 0123"
            className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 font-mono-metric outline-none focus:border-primary" />
          <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message</label>
          <textarea data-testid="wa-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={4096} placeholder="Type your message…"
            className="mt-1 w-full resize-none rounded-xl border border-input bg-background px-3 py-2.5 outline-none focus:border-primary" />
          <button data-testid="wa-send" onClick={send} disabled={busy}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
            <PaperPlaneTilt size={18} weight="fill" /> {busy ? "Sending…" : "Send WhatsApp"}
          </button>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recent messages</h2>
            {messages.length > 0 && (
              <button data-testid="wa-clear-all" onClick={clearHistory}
                className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-destructive hover:text-destructive">
                <Broom size={13} /> Clear all
              </button>
            )}
          </div>
          {messages.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No messages yet</p>}
          <div className="space-y-2">
            {messages.map((m) => (
              <div key={m.id} data-testid={`wa-msg-${m.id}`} className="rounded-xl border border-border px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-mono-metric text-sm">{m.direction === "outbound" ? "→ " : "← "}{m.direction === "outbound" ? m.to : m.from_number}</p>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{shortDate(m.created_at)}</span>
                    <button data-testid={`wa-del-${m.id}`} onClick={() => deleteMsg(m.id)} title="Delete"
                      className="text-muted-foreground transition-colors hover:text-destructive"><Trash size={13} /></button>
                  </div>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{m.text}</p>
                {m.status && <span className="mt-1 inline-block rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">{m.status}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {configured && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="wa-template-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">Start a new conversation</h2>
            <button data-testid="wa-tpl-refresh" onClick={() => { qc.invalidateQueries({ queryKey: ["wa-templates"] }); toast.info("Re-checking templates…"); }}
              className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary">
              <ArrowsClockwise size={13} /> Refresh
            </button>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">WhatsApp only lets you message someone new with a Meta-approved template. The free-form box above works only within 24 hours of the customer messaging you first.</p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Template</label>
              <select data-testid="wa-tpl-select" value={tplName}
                onChange={(e) => { setTplName(e.target.value); const t = options.find((x) => x.name === e.target.value); setTplParams(Array(paramCount(t)).fill("")); }}
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 outline-none focus:border-primary">
                <option value="">Choose a template…</option>
                {options.map((t) => <option key={t.id} value={t.name}>{t.name} ({t.language}){t.name === "hello_world" ? " — test" : ""}</option>)}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">Only <span className="font-medium">hello_world</span> (a Meta test template) is ready by default. Create more by asking iYeneh support — approved ones appear here automatically.</p>
              {allTemplates.length > 0 && (
                <div className="mt-2 space-y-1 rounded-lg border border-border bg-secondary/30 p-2" data-testid="wa-tpl-status-list">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Your templates</p>
                  {allTemplates.map((t) => (
                    <div key={t.id} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate font-mono-metric text-muted-foreground">{t.name} <span className="opacity-70">({t.language})</span></span>
                      <StatusBadge status={t.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedTpl && bodyOf(selectedTpl)?.text && (
              <p className="rounded-lg bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">{bodyOf(selectedTpl).text}</p>
            )}
            {tplParams.map((p, i) => (
              <div key={i}>
                <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Value for {`{{${i + 1}}}`}</label>
                <input data-testid={`wa-tpl-param-${i}`} value={p}
                  onChange={(e) => setTplParams((arr) => arr.map((v, idx) => (idx === i ? e.target.value : v)))}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 outline-none focus:border-primary" />
              </div>
            ))}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recipient</label>
              <input data-testid="wa-tpl-to" value={tplTo} onChange={(e) => setTplTo(e.target.value)} placeholder="+1 202 555 0123"
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 font-mono-metric outline-none focus:border-primary" />
            </div>
            <button data-testid="wa-tpl-send" onClick={sendTemplate} disabled={tplBusy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
              <PaperPlaneTilt size={18} weight="fill" /> {tplBusy ? "Sending…" : "Send template"}
            </button>
          </div>
        </div>
      )}

      {isStaff && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="wa-manage-templates">
          <h2 className="font-display text-lg font-semibold">Manage templates <span className="text-xs font-normal text-muted-foreground">(staff)</span></h2>
          {allTemplates.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {allTemplates.map((t) => (
                <div key={t.id} data-testid={`tpl-row-${t.name}`} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-sm">
                  <span className="font-mono-metric">{t.name} <span className="text-xs text-muted-foreground">({t.language})</span></span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${String(t.status).toLowerCase() === "approved" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : String(t.status).toLowerCase() === "rejected" ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"}`}>{t.status || "pending"}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 space-y-3 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Create a new template</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="tpl-name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} placeholder="welcome_message"
                className="rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
              <select data-testid="tpl-category" value={tpl.category} onChange={(e) => setTpl({ ...tpl, category: e.target.value })}
                className="rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                <option value="UTILITY">Utility</option>
                <option value="MARKETING">Marketing</option>
                <option value="AUTHENTICATION">Authentication</option>
              </select>
            </div>
            <textarea data-testid="tpl-body" value={tpl.body_text} onChange={(e) => setTpl({ ...tpl, body_text: e.target.value })} rows={3}
              placeholder="Hi {{1}}, thanks for choosing iYeneh Connect! Your order {{2}} is confirmed."
              className="w-full resize-none rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
            <input data-testid="tpl-example" value={tpl.example} onChange={(e) => setTpl({ ...tpl, example: e.target.value })} placeholder="Sample values for {{1}},{{2}} — comma separated (e.g. Alice, ORD-123)"
              className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
            <p className="text-[11px] text-muted-foreground">Use {`{{1}}`}, {`{{2}}`} for variables and give sample values so Meta can review. Approval usually takes minutes to a few hours; approved templates then appear in the sender above.</p>
            <button data-testid="tpl-create" onClick={createTemplate} disabled={creating}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.01] disabled:opacity-60">
              <Plus size={18} weight="bold" /> {creating ? "Submitting…" : "Submit for approval"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
